import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { 
  Radio, 
  Shield, 
  Server, 
  Building2,
  CheckCircle,
  ArrowRight,
  Droplets,
  Truck,
  Factory,
  Calendar,
  Layers,
  Zap,
  Settings,
  MapPin,
  Wind,
  Thermometer,
  Package,
  Users,
  Cpu,
  Globe,
  Lock,
  Signal,
  Box
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';
import { Header } from '@/components/Header';
import { TopBar } from '@/components/TopBar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/sections/ContactForm';
import { HreflangTags } from '@/components/HreflangTags';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const trackBannerClick = (target, lang) => {
  try {
    fetch(`${BACKEND_URL}/api/track/banner-click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ target, lang, page: '/tektelic' }),
      keepalive: true,
    }).catch(() => {});
  } catch (e) { /* tracking must never block navigation */ }
};

const IMAGE_BANNERS = {
  en: {
    href: 'https://tektelic.com',
    target: 'tektelic.com',
    banners: [
      {
        desktop: 'https://customer-assets-lqy194kg.emergentagent.net/job_96c739ff-ac3f-4d8c-8dce-b0efed541c1d/artifacts/gd8owovi_Banner%204%20EN.png',
        mobile: 'https://customer-assets-lqy194kg.emergentagent.net/job_96c739ff-ac3f-4d8c-8dce-b0efed541c1d/artifacts/odlxz635_Banner%202%20EN.png',
        alt: 'TEKTELIC — LoRaWAN solutions for your business: gateways, sensors and integration — tektelic.com',
        id: 'lorawan',
      },
      {
        desktop: 'https://customer-assets-lqy194kg.emergentagent.net/job_96c739ff-ac3f-4d8c-8dce-b0efed541c1d/artifacts/6xc0yq3i_Banner%201%20EN.png',
        mobile: 'https://customer-assets-lqy194kg.emergentagent.net/job_96c739ff-ac3f-4d8c-8dce-b0efed541c1d/artifacts/0uhn7l2f_Banner%203%20EN.png',
        alt: 'TEKTELIC AERIS — monitor airborne particles (PM1, PM2.5, PM10) to assess indoor air quality — tektelic.com',
        id: 'aeris',
      },
    ],
  },
  ee: {
    href: 'https://tektelic.ee',
    target: 'tektelic.ee',
    banners: [
      {
        desktop: 'https://customer-assets-lqy194kg.emergentagent.net/job_96c739ff-ac3f-4d8c-8dce-b0efed541c1d/artifacts/rktla7v8_BANNER%201%20EE.png',
        mobile: 'https://customer-assets-lqy194kg.emergentagent.net/job_96c739ff-ac3f-4d8c-8dce-b0efed541c1d/artifacts/9s77oex1_BANNER%202%20EE.png',
        alt: 'TEKTELIC Eestis — LoRaWAN-lahendused teie ettevõttele: lüüsid, andurid ja integratsioon — tektelic.ee',
        id: 'lorawan',
      },
      {
        desktop: 'https://customer-assets-lqy194kg.emergentagent.net/job_96c739ff-ac3f-4d8c-8dce-b0efed541c1d/artifacts/w8aa9n8k_BANNER%203%20EE.png',
        mobile: 'https://customer-assets-lqy194kg.emergentagent.net/job_96c739ff-ac3f-4d8c-8dce-b0efed541c1d/artifacts/nde3l30p_BANNER%204%20EE.png',
        alt: 'TEKTELIC AERIS — jälgige õhus olevaid osakesi (PM1, PM2.5, PM10) siseõhu kvaliteedi hindamiseks — tektelic.ee',
        id: 'aeris',
      },
    ],
  },
  lv: {
    href: 'https://tektelic.lv',
    target: 'tektelic.lv',
    banners: [
      {
        desktop: 'https://customer-assets-lqy194kg.emergentagent.net/job_96c739ff-ac3f-4d8c-8dce-b0efed541c1d/artifacts/w9v8r0g5_Banner%201.png',
        mobile: 'https://customer-assets-lqy194kg.emergentagent.net/job_96c739ff-ac3f-4d8c-8dce-b0efed541c1d/artifacts/utuudwvm_Banner%202.png',
        alt: 'TEKTELIC Latvijā — LoRaWAN risinājumi jūsu uzņēmumam: vārtejas, sensori un integrācija — tektelic.lv',
        id: 'lorawan',
      },
      {
        desktop: 'https://customer-assets-lqy194kg.emergentagent.net/job_96c739ff-ac3f-4d8c-8dce-b0efed541c1d/artifacts/8oh80yqb_Banner%203.png',
        mobile: 'https://customer-assets-lqy194kg.emergentagent.net/job_96c739ff-ac3f-4d8c-8dce-b0efed541c1d/artifacts/vu520e18_Banner%204.png',
        alt: 'TEKTELIC AERIS — uzraugiet gaisā esošās daļiņas (PM1, PM2.5, PM10) iekštelpu gaisa kvalitātes novērtēšanai — tektelic.lv',
        id: 'aeris',
      },
    ],
  },
  lt: {
    href: 'https://tektelic.lt',
    target: 'tektelic.lt',
    banners: [
      {
        desktop: 'https://customer-assets-lqy194kg.emergentagent.net/job_96c739ff-ac3f-4d8c-8dce-b0efed541c1d/artifacts/m6666rym_Banner%201%20LT.png',
        mobile: 'https://customer-assets-lqy194kg.emergentagent.net/job_96c739ff-ac3f-4d8c-8dce-b0efed541c1d/artifacts/zyqziiz4_Banner%202%20LT.png',
        alt: 'TEKTELIC Lietuvoje — LoRaWAN sprendimai jūsų verslui: tinklų sietuvai, jutikliai ir integracija — tektelic.lt',
        id: 'lorawan',
      },
      {
        desktop: 'https://customer-assets-lqy194kg.emergentagent.net/job_96c739ff-ac3f-4d8c-8dce-b0efed541c1d/artifacts/theh9s2d_Banner%203%20LT.png',
        mobile: 'https://customer-assets-lqy194kg.emergentagent.net/job_96c739ff-ac3f-4d8c-8dce-b0efed541c1d/artifacts/ib6s18gh_Banner%204%20LT.png',
        alt: 'TEKTELIC AERIS — stebėkite ore esančias daleles (PM1, PM2.5, PM10) patalpų oro kokybei vertinti — tektelic.lt',
        id: 'aeris',
      },
    ],
  },
};

const BALTICS_BANNER = {
  en: {
    title: 'TEKTELIC in the Baltics',
    text: 'Looking for local TEKTELIC solutions, technical support and project integration?',
    links: [
      { href: 'https://tektelic.lv', label: 'Latvia → tektelic.lv' },
      { href: 'https://tektelic.lt', label: 'Lithuania → tektelic.lt' },
      { href: 'https://tektelic.ee', label: 'Estonia → tektelic.ee' },
    ],
  },
  lv: {
    title: 'TEKTELIC Latvijā',
    text: 'Meklējat TEKTELIC risinājumus, tehnisko atbalstu un projektu integrāciju Latvijā?',
    links: [{ href: 'https://tektelic.lv', label: 'Apmeklējiet tektelic.lv →' }],
  },
  lt: {
    title: 'TEKTELIC Lietuvoje',
    text: 'Ieškote TEKTELIC sprendimų, techninės pagalbos ir projektų integravimo Lietuvoje?',
    links: [{ href: 'https://tektelic.lt', label: 'Apsilankykite tektelic.lt →' }],
  },
  ee: {
    title: 'TEKTELIC Eestis',
    text: 'Kas otsite Eestis TEKTELICu lahendusi, tehnilist tuge ja projektide integratsiooni?',
    links: [{ href: 'https://tektelic.ee', label: 'Külastage kohalikku TEKTELICu lehte →' }],
  },
};

const TektelicPage = () => {
  const { t, language } = useLanguage();
  const location = useLocation();
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    setSlide(0);
    if (!IMAGE_BANNERS[language]) return;
    const timer = setInterval(() => {
      setSlide((s) => (s + 1) % IMAGE_BANNERS[language].banners.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [language]);

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToKits = () => {
    document.getElementById('starter-kits')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Tektelic LoRaWAN Infrastructure | Carrier-Grade IoT Networks for Cities and Enterprises | 0g Baltics</title>
        <meta name="description" content="Deploy carrier-grade LoRaWAN infrastructure using Tektelic gateways with 0g Baltics. Ideal for smart cities, utilities, logistics and industrial IoT deployments." />
        <meta name="keywords" content="LoRaWAN infrastructure, Tektelic gateways, LoRaWAN network deployment, LoRaWAN smart city infrastructure, LoRaWAN utilities network, carrier grade LoRaWAN" />
        <link rel="canonical" href="https://0gbaltics.com/tektelic" />
        <meta property="og:title" content="Tektelic LoRaWAN Infrastructure | 0g Baltics" />
        <meta property="og:description" content="Deploy carrier-grade LoRaWAN infrastructure using Tektelic gateways for smart cities, utilities and industrial IoT." />
        <meta property="og:type" content="website" />
      </Helmet>
      <HreflangTags />

      <TopBar />
      <Header />
      
      <main className="pt-20">
        {/* SECTION 1 - HERO */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="tektelic-hero">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center space-x-2 mb-6">
                  <span className="tech-badge bg-teal-500/20 border-teal-500/30 text-teal-400">{t('tektelicPage.heroBadge')}</span>
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  {t('tektelicPage.heroTitle')}{' '}
                  <span className="text-teal-400">{t('tektelicPage.heroTitleHighlight')}</span>
                </h1>
                <p className="text-xl text-slate-300 mb-6 leading-relaxed">
                  {t('tektelicPage.heroSubtitle')}
                </p>
                <p className="text-lg text-slate-400 mb-8 leading-relaxed">
                  {t('tektelicPage.heroDesc')}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    className="btn-primary inline-flex items-center justify-center gap-2 text-base"
                    onClick={scrollToContact}
                    data-testid="tektelic-cta-primary"
                  >
                    <Calendar className="h-5 w-5" />
                    {t('tektelicPage.ctaPrimary')}
                  </Button>
                  <Button 
                    variant="outline"
                    className="border-2 border-white/30 text-white hover:bg-white/10 px-6 py-3 font-semibold inline-flex items-center justify-center gap-2 text-base"
                    onClick={scrollToKits}
                    data-testid="tektelic-cta-secondary"
                  >
                    {t('tektelicPage.ctaSecondary')}
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                </div>
              </div>
              
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_industrial-iot-4/artifacts/m59g09fr_macro-photo-1.png%20%281%29.jpeg"
                  alt="Tektelic LoRaWAN gateway deployed in the field"
                  className="w-full h-[400px] lg:h-[500px] object-cover rounded-sm"
                />
                <div className="absolute top-6 right-6 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-sm shadow-lg">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">{t('tektelicPage.heroBadge')}</p>
                  <p className="text-lg font-bold text-slate-900">TEKTELIC</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TEKTELIC BALTICS BANNER */}
        {IMAGE_BANNERS[language] ? (
          <section className="relative bg-slate-800" data-testid="tektelic-baltics-banner">
            <div className="grid">
              {IMAGE_BANNERS[language].banners.map((b, i) => (
                <a
                  key={b.id}
                  href={IMAGE_BANNERS[language].href}
                  target="_blank"
                  rel="noopener"
                  onClick={() => trackBannerClick(IMAGE_BANNERS[language].target, language)}
                  className={`col-start-1 row-start-1 block transition-opacity duration-1000 ${i === slide ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}
                  aria-hidden={i !== slide}
                  data-testid={`tektelic-${language}-banner-${b.id}`}
                >
                  <img src={b.desktop} alt={b.alt} loading="lazy" className="hidden sm:block w-full" />
                  <img src={b.mobile} alt={b.alt} loading="lazy" className="sm:hidden w-full" />
                </a>
              ))}
            </div>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex gap-2">
              {IMAGE_BANNERS[language].banners.map((b, i) => (
                <button
                  key={b.id}
                  onClick={() => setSlide(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={`h-2.5 rounded-full transition-all duration-300 ${i === slide ? 'w-7 bg-[#14233F]' : 'w-2.5 bg-[#14233F]/30 hover:bg-[#14233F]/60'}`}
                  data-testid={`tektelic-banner-dot-${i}`}
                />
              ))}
            </div>
          </section>
        ) : (
          <section className="bg-slate-800 border-y border-teal-500/30" data-testid="tektelic-baltics-banner">
            <div className="container-0g py-6 md:py-8">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
                <div className="flex-1">
                  <p className="text-teal-400 font-bold text-lg md:text-xl mb-1">{BALTICS_BANNER[language].title}</p>
                  <p className="text-slate-300 text-sm md:text-base leading-relaxed">{BALTICS_BANNER[language].text}</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                  {BALTICS_BANNER[language].links.map((l) => (
                    <a
                      key={l.href}
                      href={l.href}
                      target="_blank"
                      rel="noopener"
                      onClick={() => trackBannerClick(l.href.replace('https://', ''), language)}
                      className="inline-flex items-center justify-center gap-2 border border-teal-500/50 bg-teal-500/10 hover:bg-teal-500/20 text-white px-5 py-2.5 rounded-sm font-semibold text-sm transition-colors"
                      data-testid={`tektelic-baltics-link-${l.href.replace('https://', '').replace('.', '-')}`}
                    >
                      {l.label}
                      <ArrowRight className="h-4 w-4 text-teal-400" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* SECTION 2 - WHY TEKTELIC */}
        <section className="py-20 md:py-32 bg-white" data-testid="why-tektelic">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <p className="section-label">{t('tektelicPage.whyBadge')}</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('tektelicPage.whyTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('tektelicPage.whyDesc')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Shield, title: t('tektelicPage.feature1Title'), desc: t('tektelicPage.feature1Desc') },
                { icon: Server, title: t('tektelicPage.feature2Title'), desc: t('tektelicPage.feature2Desc') },
                { icon: Layers, title: t('tektelicPage.feature3Title'), desc: t('tektelicPage.feature3Desc') },
                { icon: Settings, title: t('tektelicPage.feature4Title'), desc: t('tektelicPage.feature4Desc') },
                { icon: Lock, title: t('tektelicPage.feature5Title'), desc: t('tektelicPage.feature5Desc') },
                { icon: Signal, title: t('tektelicPage.feature6Title'), desc: t('tektelicPage.feature6Desc') },
              ].map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div 
                    key={index}
                    className="bg-slate-50 border border-slate-200 p-6 rounded-sm hover:border-teal-500 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-sm bg-teal-600 flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-sm text-slate-600">{feature.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* SECTION 3 - LORAWAN FOR CITIES */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="lorawan-cities">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">{t('tektelicPage.citiesBadge')}</p>
                <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                  {t('tektelicPage.citiesTitle')}
                </h2>
                <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                  {t('tektelicPage.citiesDesc')}
                </p>
                
                <div className="bg-slate-800 border border-slate-700 p-6 rounded-sm">
                  <h3 className="text-lg font-semibold text-white mb-4">{t('tektelicPage.networkEfficiency')}</h3>
                  <p className="text-slate-400 text-sm">
                    {t('tektelicPage.networkEfficiencyDesc')}
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-6">{t('tektelicPage.useCasesTitle')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { icon: Droplets, title: t('tektelicPage.useCase1Title'), desc: t('tektelicPage.useCase1Desc') },
                    { icon: Wind, title: t('tektelicPage.useCase2Title'), desc: t('tektelicPage.useCase2Desc') },
                    { icon: MapPin, title: t('tektelicPage.useCase3Title'), desc: t('tektelicPage.useCase3Desc') },
                    { icon: Truck, title: t('tektelicPage.useCase4Title'), desc: t('tektelicPage.useCase4Desc') },
                    { icon: Package, title: t('tektelicPage.useCase5Title'), desc: t('tektelicPage.useCase5Desc') },
                    { icon: Thermometer, title: t('tektelicPage.useCase6Title'), desc: t('tektelicPage.useCase6Desc') },
                    { icon: Building2, title: t('tektelicPage.useCase7Title'), desc: t('tektelicPage.useCase7Desc') },
                  ].map((useCase, index) => {
                    const Icon = useCase.icon;
                    return (
                      <div key={index} className="flex items-start space-x-3 bg-slate-800 border border-slate-700 p-4 rounded-sm">
                        <div className="w-10 h-10 rounded-sm bg-teal-600/20 flex items-center justify-center flex-shrink-0">
                          <Icon className="h-5 w-5 text-teal-400" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-white text-sm">{useCase.title}</h4>
                          <p className="text-xs text-slate-400">{useCase.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4 - MULTI NETWORK APPROACH */}
        <section className="py-20 md:py-32 bg-white" data-testid="hybrid-infrastructure">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <p className="section-label">{t('tektelicPage.multiNetworkBadge')}</p>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('tektelicPage.multiNetworkTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('tektelicPage.multiNetworkDesc')}
                </p>
                
                <div className="space-y-4">
                  {[
                    { icon: Zap, title: t('tektelicPage.benefit1Title'), desc: t('tektelicPage.benefit1Desc') },
                    { icon: Layers, title: t('tektelicPage.benefit2Title'), desc: t('tektelicPage.benefit2Desc') },
                    { icon: Shield, title: t('tektelicPage.benefit3Title'), desc: t('tektelicPage.benefit3Desc') },
                    { icon: Settings, title: t('tektelicPage.benefit4Title'), desc: t('tektelicPage.benefit4Desc') },
                  ].map((benefit, index) => {
                    const Icon = benefit.icon;
                    return (
                      <div key={index} className="flex items-start space-x-4">
                        <div className="w-10 h-10 rounded-sm bg-teal-100 flex items-center justify-center flex-shrink-0">
                          <Icon className="h-5 w-5 text-teal-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">{benefit.title}</h4>
                          <p className="text-sm text-slate-600">{benefit.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="mt-8">
                  <Link 
                    to="/connectivity"
                    className="inline-flex items-center text-teal-600 font-medium hover:underline"
                  >
                    {t('tektelicPage.learnMultiNetwork')}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </div>
              </div>
              
              <div className="bg-slate-50 border border-slate-200 p-8 rounded-sm">
                <h3 className="text-xl font-semibold text-slate-900 mb-6">{t('tektelicPage.connectivityTechnologies')}</h3>
                <div className="space-y-4">
                  {[
                    { name: t('tektelicPage.lorawanName'), desc: t('tektelicPage.lorawanDesc'), primary: true },
                    { name: t('tektelicPage.sigfoxName'), desc: t('tektelicPage.sigfoxDesc'), primary: false },
                    { name: t('tektelicPage.nbiotName'), desc: t('tektelicPage.nbiotDesc'), primary: false },
                    { name: t('tektelicPage.bleName'), desc: t('tektelicPage.bleDesc'), primary: false },
                    { name: t('tektelicPage.satelliteName'), desc: t('tektelicPage.satelliteDesc'), primary: false },
                  ].map((tech, index) => (
                    <div 
                      key={index} 
                      className={`flex items-center justify-between p-3 rounded-sm ${tech.primary ? 'bg-teal-600 text-white' : 'bg-white border border-slate-200'}`}
                    >
                      <span className={`font-medium ${tech.primary ? 'text-white' : 'text-slate-900'}`}>{tech.name}</span>
                      <span className={`text-sm ${tech.primary ? 'text-teal-100' : 'text-slate-500'}`}>{tech.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 5 - STARTER KITS */}
        <section id="starter-kits" className="py-20 md:py-32 bg-slate-50" data-testid="starter-kits">
          <div className="container-0g">
            <div className="text-center mb-12">
              <p className="section-label">{t('tektelicPage.kitsBadge')}</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
                {t('tektelicPage.kitsTitle')}
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                {t('tektelicPage.kitsDesc')}
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {/* Kit 1 - Pilot */}
              <div className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:border-teal-500 transition-colors">
                <div className="bg-slate-900 p-6">
                  <div className="w-12 h-12 rounded-sm bg-teal-600 flex items-center justify-center mb-4">
                    <Cpu className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{t('tektelicPage.pilotKitTitle')}</h3>
                  <p className="text-slate-400 text-sm">{t('tektelicPage.pilotKitDesc')}</p>
                </div>
                <div className="p-6">
                  <h4 className="font-semibold text-slate-900 mb-3">{t('tektelicPage.pilotIncludes')}</h4>
                  <ul className="space-y-2 mb-6">
                    {[
                      t('tektelicPage.pilotItem1'),
                      t('tektelicPage.pilotItem2'),
                      t('tektelicPage.pilotItem3'),
                      t('tektelicPage.pilotItem4'),
                    ].map((item, i) => (
                      <li key={i} className="flex items-start space-x-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-teal-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <h4 className="font-semibold text-slate-900 mb-2">{t('tektelicPage.pilotUseCases')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {[t('tektelicPage.pilotTag1'), t('tektelicPage.pilotTag2'), t('tektelicPage.pilotTag3')].map((tag, i) => (
                      <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Kit 2 - Smart City */}
              <div className="bg-white border-2 border-teal-500 rounded-sm overflow-hidden relative">
                <div className="absolute top-4 right-4 bg-teal-600 text-white text-xs font-bold px-2 py-1 rounded">{t('tektelicPage.popular')}</div>
                <div className="bg-slate-900 p-6">
                  <div className="w-12 h-12 rounded-sm bg-teal-600 flex items-center justify-center mb-4">
                    <Building2 className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{t('tektelicPage.smartCityKitTitle')}</h3>
                  <p className="text-slate-400 text-sm">{t('tektelicPage.smartCityKitDesc')}</p>
                </div>
                <div className="p-6">
                  <h4 className="font-semibold text-slate-900 mb-3">{t('tektelicPage.smartCityIncludes')}</h4>
                  <ul className="space-y-2 mb-6">
                    {[
                      t('tektelicPage.smartCityItem1'),
                      t('tektelicPage.smartCityItem2'),
                      t('tektelicPage.smartCityItem3'),
                      t('tektelicPage.smartCityItem4'),
                    ].map((item, i) => (
                      <li key={i} className="flex items-start space-x-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-teal-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <h4 className="font-semibold text-slate-900 mb-2">{t('tektelicPage.smartCityUseCases')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {[t('tektelicPage.smartCityTag1'), t('tektelicPage.smartCityTag2'), t('tektelicPage.smartCityTag3'), t('tektelicPage.smartCityTag4')].map((tag, i) => (
                      <span key={i} className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Kit 3 - Industrial */}
              <div className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:border-teal-500 transition-colors">
                <div className="bg-slate-900 p-6">
                  <div className="w-12 h-12 rounded-sm bg-teal-600 flex items-center justify-center mb-4">
                    <Factory className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{t('tektelicPage.industrialKitTitle')}</h3>
                  <p className="text-slate-400 text-sm">{t('tektelicPage.industrialKitDesc')}</p>
                </div>
                <div className="p-6">
                  <h4 className="font-semibold text-slate-900 mb-3">{t('tektelicPage.industrialIncludes')}</h4>
                  <ul className="space-y-2 mb-6">
                    {[
                      t('tektelicPage.industrialItem1'),
                      t('tektelicPage.industrialItem2'),
                      t('tektelicPage.industrialItem3'),
                      t('tektelicPage.industrialItem4'),
                    ].map((item, i) => (
                      <li key={i} className="flex items-start space-x-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-teal-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <h4 className="font-semibold text-slate-900 mb-2">{t('tektelicPage.industrialUseCases')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {[t('tektelicPage.industrialTag1'), t('tektelicPage.industrialTag2'), t('tektelicPage.industrialTag3')].map((tag, i) => (
                      <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center mt-8">
              <Button 
                className="btn-primary"
                onClick={scrollToContact}
              >
                {t('tektelicPage.requestKitInfo')}
              </Button>
            </div>
          </div>
        </section>

        {/* SECTION 6 - DEVICE ECOSYSTEM */}
        <section className="py-20 md:py-32 bg-white" data-testid="device-ecosystem">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <p className="section-label">{t('tektelicPage.devicesBadge')}</p>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('tektelicPage.devicesTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  {t('tektelicPage.devicesDesc')}
                </p>
                
                <Link 
                  to="/#devices"
                  className="inline-flex items-center text-teal-600 font-medium hover:underline"
                >
                  {t('tektelicPage.viewDeviceEcosystem')}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  { name: t('tektelicPage.device1Name'), desc: t('tektelicPage.device1Desc') },
                  { name: t('tektelicPage.device2Name'), desc: t('tektelicPage.device2Desc') },
                  { name: t('tektelicPage.device3Name'), desc: t('tektelicPage.device3Desc') },
                  { name: t('tektelicPage.device4Name'), desc: t('tektelicPage.device4Desc') },
                  { name: t('tektelicPage.device5Name'), desc: t('tektelicPage.device5Desc') },
                  { name: t('tektelicPage.device6Name'), desc: t('tektelicPage.device6Desc') },
                ].map((device, index) => (
                  <div 
                    key={index}
                    className="bg-slate-50 border border-slate-200 p-4 rounded-sm hover:border-teal-500 transition-colors"
                  >
                    <h4 className="font-semibold text-slate-900 mb-1">{device.name}</h4>
                    <p className="text-sm text-slate-500">{device.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 7 - CONSULTING AND DEPLOYMENT */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="consulting-deployment">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <p className="text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">{t('tektelicPage.servicesBadge')}</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                {t('tektelicPage.servicesTitle')}
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed">
                {t('tektelicPage.servicesDesc')}
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {[
                { icon: MapPin, title: t('tektelicPage.service1') },
                { icon: Server, title: t('tektelicPage.service2') },
                { icon: Signal, title: t('tektelicPage.service3') },
                { icon: Cpu, title: t('tektelicPage.service4') },
                { icon: Zap, title: t('tektelicPage.service5') },
                { icon: Box, title: t('tektelicPage.service6') },
                { icon: Globe, title: t('tektelicPage.service7') },
              ].map((service, index) => {
                const Icon = service.icon;
                return (
                  <div 
                    key={index}
                    className="bg-slate-800 border border-slate-700 p-4 rounded-sm text-center hover:border-teal-500 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-sm bg-teal-600/20 flex items-center justify-center mx-auto mb-3">
                      <Icon className="h-5 w-5 text-teal-400" />
                    </div>
                    <h4 className="text-sm font-medium text-white">{service.title}</h4>
                  </div>
                );
              })}
            </div>
            
            <div className="text-center mt-8">
              <Link 
                to="/#consulting"
                className="inline-flex items-center text-teal-400 font-medium hover:underline"
              >
                {t('tektelicPage.learnConsulting')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </div>
          </div>
        </section>

        {/* SECTION 8 - WHO WE WORK WITH */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="who-we-work-with">
          <div className="container-0g">
            <div className="text-center mb-12">
              <p className="section-label">{t('tektelicPage.clientsBadge')}</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
                {t('tektelicPage.clientsTitle')}
              </h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {[
                { icon: Building2, title: t('tektelicPage.client1Title'), desc: t('tektelicPage.client1Desc') },
                { icon: Droplets, title: t('tektelicPage.client2Title'), desc: t('tektelicPage.client2Desc') },
                { icon: Factory, title: t('tektelicPage.client3Title'), desc: t('tektelicPage.client3Desc') },
                { icon: Truck, title: t('tektelicPage.client4Title'), desc: t('tektelicPage.client4Desc') },
                { icon: Users, title: t('tektelicPage.client5Title'), desc: t('tektelicPage.client5Desc') },
              ].map((client, index) => {
                const Icon = client.icon;
                return (
                  <div 
                    key={index}
                    className="bg-white border border-slate-200 p-6 rounded-sm text-center hover:border-teal-500 transition-colors"
                  >
                    <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-7 w-7 text-teal-600" />
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-1">{client.title}</h3>
                    <p className="text-sm text-slate-500">{client.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* SECTION 9 - CTA */}
        <section className="py-20 md:py-32 cta-gradient" data-testid="tektelic-cta">
          <div className="container-0g">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                {t('tektelicPage.ctaTitle')}
              </h2>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                {t('tektelicPage.ctaDesc')}
              </p>
              <Button 
                className="btn-primary bg-teal-500 hover:bg-teal-400 text-white px-8 py-4 text-lg inline-flex items-center gap-2"
                onClick={scrollToContact}
                data-testid="tektelic-final-cta"
              >
                <Calendar className="h-5 w-5" />
                {t('tektelicPage.ctaButton')}
              </Button>
            </div>
          </div>
        </section>

        {/* CONTACT FORM */}
        <ContactForm />
      </main>

      <Footer />
    </>
  );
};

export default TektelicPage;
